import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Table, Button, Icon } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { Area } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { AreaForm } from "../AreaForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListAreas.scss";

const areaController = new Area();

export function ListAreas({ areasActive, reload, onReload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [areas,       setAreas]       = useState(null);
  const [areasFilter, setAreasFilter] = useState([]);
  const [search,      setSearch]      = useState("");
  const [showModal,   setShowModal]   = useState(false);
  const [selectedArea,setSelectedArea]= useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMsg,  setConfirmMsg]  = useState("");
  const [isDelete,    setIsDelete]    = useState(false);
  const [rowMenu,     setRowMenu]     = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!rowMenu) return;
    const handler = (e) => {
      if (!e.target.closest(".areas-list__row-menu, .areas-list__row-menu-list")) setRowMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowMenu]);

  const toggleRowMenu = (id, e) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setRowMenu({
      id,
      left: rect.right - 170,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setAreas(null);
        const res = await areaController.getAreas(accessToken, areasActive);
        setAreas(res);
        setAreasFilter(res);
      } catch {
        setAreas([]);
        setAreasFilter([]);
      }
    })();
  }, [areasActive, reload]);

  useEffect(() => {
    if (!areas) return;
    if (!search) { setAreasFilter(areas); return; }
    const q = search.toLowerCase();
    setAreasFilter(
      areas.filter((a) =>
        a.code?.toLowerCase().includes(q) || a.name?.toLowerCase().includes(q)
      )
    );
  }, [search, areas]);

  const openCreate = () => { setSelectedArea(null); setShowModal(true); };
  const openEdit   = (a)  => { setSelectedArea(a);   setShowModal(true); };

  const openToggle = (a) => {
    setSelectedArea(a);
    setIsDelete(false);
    setConfirmMsg(`¿${a.active ? t("deactivate") : t("activate")} "${a.name}"?`);
    setShowConfirm(true);
  };

  const openDelete = (a) => {
    setSelectedArea(a);
    setIsDelete(true);
    setConfirmMsg(`¿${t("delete")} "${a.name}"?`);
    setShowConfirm(true);
  };

  const handleToggle = async () => {
    try {
      await areaController.updateArea(accessToken, selectedArea._id, { active: !selectedArea.active });
      toast.success(selectedArea.active ? t("area_deactivated") : t("area_activated"));
      setShowConfirm(false);
      onReload();
    } catch { toast.error(t("error_updating")); }
  };

  const handleDelete = async () => {
    try {
      await areaController.deleteArea(accessToken, selectedArea._id);
      toast.success(t("area_deleted"));
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || t("error_deleting")); }
  };

  if (areas === null) return <LoadingSpinner />;

  return (
    <div className="areas-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="areas-list__card">
        <div className="areas-list__filters">
          <div className="search-user-wrapper">
            <img src={searchSvg} alt="search" className="search-user-icon" />
            <input
              className="search-user-input"
              placeholder={`${t("search")}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button icon className="add-area-btn" title={t("create")} onClick={openCreate}>
            <Icon name="plus" />
          </Button>
        </div>

        {areasFilter.length === 0 ? (
          <div className="list-empty-state">
                <Icon name="folder open outline" className="list-empty-state__icon" />
                <p className="list-empty-state__text">{t("no_results")}</p>
              </div>
        ) : (
          <div className="areas-list__tbl-scroll">
            <Table className="areas-table unstackable rtable-cards row-hover">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("code")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("name")}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {areasFilter.map((a) => (
                  <Table.Row key={a._id}>
                    <Table.Cell data-label={t("code")}>{a.code ? a.code.toUpperCase() : ""}</Table.Cell>
                    <Table.Cell data-label={t("name")}>{a.name}</Table.Cell>
                    <Table.Cell textAlign="center" data-label="" className="areas-table__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="areas-list__row-menu">
                        <button type="button" className="areas-list__row-menu-btn" onClick={(e) => toggleRowMenu(a._id, e)} aria-label={t("actions")}>
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {rowMenu?.id === a._id && ReactDOM.createPortal(
                        <div
                          className="areas-list__row-menu-list"
                          style={{
                            position: "fixed",
                            top: rowMenu.top,
                            left: rowMenu.left,
                            transform: rowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { openEdit(a); setRowMenu(null); }}>
                            <Icon name="pencil" />{t("edit")}
                          </button>
                          <button onClick={() => { openToggle(a); setRowMenu(null); }}>
                            <Icon name={a.active ? "ban" : "check"} />{a.active ? t("deactivate") : t("activate")}
                          </button>
                          <button className="areas-list__row-menu-item--danger" onClick={() => { openDelete(a); setRowMenu(null); }}>
                            <Icon name="trash" />{t("delete")}
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selectedArea ? `${t("edit")}: ${selectedArea.name}` : t("new_area")}
        size="small"
        className="catalog-modal"
      >
        <AreaForm
          area={selectedArea}
          close={() => setShowModal(false)}
          onReload={onReload}
        />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? "Eliminar" : (selectedArea?.active ? "Desactivar" : "Activar")}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel="Aceptar"
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />
    </div>
  );
}
